/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { isValid, parseISO } from 'date-fns';
import { useEffect } from 'react';
import type {
  IAuditImpactAreaItem,
  IAuditUrgencyItem,
} from '@/api/report/types';
import { useAuditTypes } from '@/app/(client)/audits/-hooks/useAuditsQuery';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  useCollaborationStatusOptions,
  useCountryOptions,
  useNetworkCompanyOptions,
} from '@/hooks/useDropdowns';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { useSessionStore } from '@/store/sessionStore';
import { AuditFindingsUrgencyChart } from '../../../-components/audit-compliance/AuditFindingsUrgencyChart';
import { AuditKpiGrid } from '../../../-components/audit-compliance/AuditKpiGrid';
import { AuditsByImpactAreasTable } from '../../../-components/audit-compliance/AuditsByImpactAreasTable';
import { AuditsByStandardChart } from '../../../-components/audit-compliance/AuditsByStandardChart';
import { AuditsByTypeChart } from '../../../-components/audit-compliance/AuditsByTypeChart';
import { dateRangeRenderFilter } from '../../../-components/dateRangeRenderFilter';
import { ReportToolbar } from '../../../-components/ReportToolbar';
import {
  useAuditMetricsQuery,
  useAuditUrgenciesQuery,
} from '../../../-hooks/useAuditComplianceQuery';
import { useAuditFilters } from '../../../-hooks/useAuditFilters';
import { useReportFilterPreferences } from '../../../-hooks/useReportFilterPreferences';
import { flattenFacilityProcessGroups } from '../../../-utils/filterDefs';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(analytics)/audit-compliance/',
)({
  component: AuditCompliancePage,
});

function AuditCompliancePage() {
  const { getTranslation: t } = useTranslator({ page: 'auditCompliance' });
  const navigate = useNavigate();
  const _companyId = useSessionStore(s => s.user?.companyId ?? '');

  const suppliers = useNetworkCompanyOptions();
  const collabStatuses = useCollaborationStatusOptions();
  const processes = useGroupedFacilityProcessOptions();
  const countries = useCountryOptions();
  const auditTypesResult = useAuditTypes();
  const auditTypeOptions = (auditTypesResult.data?.data ?? []).map(at => ({
    key: at.id,
    label: at.name,
  }));

  const { filters, setFilters, appliedFilter, filterDefs } = useAuditFilters({
    supplierOptions: suppliers.data ?? [],
    collaborationStatusOptions: collabStatuses.data ?? [],
    processOptions: flattenFacilityProcessGroups(processes.data ?? []),
    countryOptions: countries.data ?? [],
    auditTypeOptions,
    dateRangeRenderFilter,
  });

  const { saveFilter } = useReportFilterPreferences('rpt-audit-compliance');
  useEffect(() => {
    saveFilter(appliedFilter);
  }, [appliedFilter, saveFilter]);

  const { data: metricsData, isLoading } = useAuditMetricsQuery(appliedFilter);
  const { data: urgenciesData } = useAuditUrgenciesQuery(appliedFilter);

  const DRILLDOWN_ALLOWED_KEYS = new Set([
    'supplierCompanyIds',
    'collaborationStatusIds',
    'facilityProcesses',
    'countries',
    'auditTypeIds',
    'auditTypeImpactAreaId',
    'auditFindingUrgency',
    'startDate',
    'endDate',
    'upcomingAudits',
  ]);

  const openDrilldown = (
    chartType: string,
    extra?: Record<string, unknown>,
  ) => {
    const base = (() => {
      try {
        return JSON.parse(appliedFilter) as Record<string, unknown>;
      } catch {
        return {};
      }
    })();
    const merged = { ...base, ...extra };
    const drilldownFilter = Object.fromEntries(
      Object.entries(merged).filter(([k]) => DRILLDOWN_ALLOWED_KEYS.has(k)),
    );
    void navigate({
      to: '/report/audit-compliance/drilldown/$chartType',
      params: { chartType },
      search: { filter: JSON.stringify(drilldownFilter) },
    });
  };

  const handleUrgencyBarClick = (
    urgencyId: string,
    isPrevious: boolean,
    item: IAuditUrgencyItem,
  ) => {
    const extra: Record<string, unknown> = { auditFindingUrgency: urgencyId };
    if (isPrevious) {
      const { startDate, endDate } = item.previousCountDateRange;
      if (
        startDate &&
        endDate &&
        isValid(parseISO(startDate)) &&
        isValid(parseISO(endDate))
      ) {
        extra.startDate = startDate;
        extra.endDate = endDate;
      }
    }
    openDrilldown('auditFindingsByUrgency', extra);
  };

  const handleImpactAreaDrilldown = (row: IAuditImpactAreaItem) => {
    openDrilldown('auditByImpactAreas', {
      hierarchyId: row.hierarchyId,
      impactArea: row.impactArea,
      auditTypeImpactAreaId: row.impactAreaId,
    });
  };

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-3 z-10'>
        <ReportToolbar
          filterDefs={filterDefs}
          filters={filters}
          onFiltersChange={setFilters}
          helpUrl='https://app.intercom.com/a/apps/11060514-introduction-to-the-audit-compliance-report'
        />
      </div>

      <div className='flex-1 min-h-0 overflow-y-auto'>
        <div className='flex flex-col gap-6 p-4 pb-10'>
          <AuditKpiGrid
            data={metricsData}
            loading={isLoading}
            onDrilldown={openDrilldown}
            auditTypeFilterActive={!!filters.auditTypeIds?.length}
          />

          {/* Historic data section */}
          <div className='flex flex-col gap-1'>
            <p className='text-sm font-semibold text-foreground leading-tight'>
              {t('historicData.title')}
            </p>
            <p className='text-sm text-muted-foreground'>
              {t('historicData.description')}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <AuditsByTypeChart
              data={metricsData?.auditsByType}
              loading={isLoading}
            />
            <AuditsByStandardChart
              data={metricsData?.auditsByStandard}
              loading={isLoading}
            />
          </div>

          <AuditFindingsUrgencyChart
            data={urgenciesData?.data}
            loading={isLoading}
            onBarClick={handleUrgencyBarClick}
          />

          <AuditsByImpactAreasTable
            filter={appliedFilter}
            onDrilldown={handleImpactAreaDrilldown}
          />
        </div>
      </div>
    </PageContainer>
  );
}
